/* eslint-disable max-lines */
import intersection from 'lodash/intersection';

import type { CompanyLinkDefaultType } from './auth.types';
import type { UserType, CompanyType } from 'api/api.types';

type CheckAccessCompanyType = CompanyType | null;
type CheckAccessUserType = UserType | null;

const ADMIN_ROLES = ['admin'];
const CURATOR_ROLES = ['curator', 'admin'];

export function checkAdminAccess(
  user: UserType | null,
  visibilityGroup: number | null = null,
) {
  return (
    visibilityGroup === null && !!intersection(user?.roles, ADMIN_ROLES).length
  );
}

export function checkImpersonatingOrStaffAccess(user: UserType | null) {
  return !!user?.isStaff || !!user?.canImpersonate || !!user?.isImpersonating;
}

export function checkCuratorAccess(
  user: UserType | null,
  visibilityGroup: number | null = null,
) {
  return (
    visibilityGroup === null &&
    !!intersection(user?.roles, CURATOR_ROLES).length
  );
}

export function checkReportsAccess(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
) {
  if (!company || !user) return false;

  const isAdmin = checkAdminAccess(user, visibilityGroup);
  const isCurator = checkCuratorAccess(user, visibilityGroup);
  const curatorCanSeeReports =
    isCurator &&
    (checkIsCRAReportEnabledForCurators(company) ||
      checkIsConsumerReportEnabledForCurators(company) ||
      checkIsCuratorReportEnabledForCurators(company) ||
      checkIsAskKlueExportEnabledForCurators(company));

  return Boolean(
    (isAdmin || curatorCanSeeReports) &&
    Object.values(company?.usageReports ?? {}).length,
  );
}

export function checkTagVisibility(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null,
) {
  if (!company || !user) return false;

  const isCurator = checkCuratorAccess(user, visibilityGroup);
  const isConsumersAllowed = !!company.companyData.cardTagIsConsumerVisible;

  return isCurator || isConsumersAllowed;
}

export function checkCardInfoVisibility(company: CheckAccessCompanyType) {
  if (!company) return false;

  let showCardInfo = company?.companyData?.showCardInfo;
  if (typeof showCardInfo === 'undefined') showCardInfo = true;
  return showCardInfo;
}

export function checkImpersonating(user: CheckAccessUserType) {
  return Boolean(user?.isImpersonating);
}

export function checkDashboardNewsAccess(company: CheckAccessCompanyType) {
  if (!company) return false;
  return !company.companyData.isDashboardNewsDisabled;
}

export function checkCardFeedbackDisabled(company: CheckAccessCompanyType) {
  return Boolean(company?.companyData?.isCardFeedbackDisabled);
}

export function checkCardSentimentAccess(company: CheckAccessCompanyType) {
  return !company?.companyData?.isCardSentimentDisabled;
}

export function checkCardSentimentAndFeedbackOnHoverDisabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(
    company?.companyData?.isCardSentimentAndFeedbackOnHoverDisabled,
  );
}

/**
 * Visibility-group allowlist for Win-Loss, mirroring askKluePermittedVisibilityGroups.
 *
 * Absent or not an array means everyone passes, so companies that never configure it keep
 * the behaviour they have today. An empty array means no consumer — an explicitly empty
 * allowlist is "no one", not "everyone", the same reading Ask Klue uses.
 *
 * Curators are exempt, and structurally so: visibility groups are consumer-only by design,
 * so a curator's visibilityGroupId is always null and an allowlist keyed on group IDs
 * cannot express them. Without this, configuring one would hide Win-Loss from every
 * curator. It does not weaken the control — the partners it exists to keep out are
 * consumers.
 *
 * This is the UI half of a rule that is enforced server-side in User#win_loss_access?;
 * hiding the menu is a courtesy, the /api/winloss proxy is what actually protects the data.
 */
export function checkWinLossVisibilityGroupPermitted(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
): boolean {
  if (checkCuratorAccess(user, visibilityGroup)) {
    return true;
  }

  const permittedGroups =
    company?.companyData?.winLossPermittedVisibilityGroups;

  if (
    permittedGroups === undefined ||
    !Array.isArray(permittedGroups) ||
    !permittedGroups.every((id) => typeof id === 'number')
  ) {
    return true;
  }

  const effectiveVisibilityGroupId =
    visibilityGroup !== null ? visibilityGroup : user?.visibilityGroupId;

  return (
    effectiveVisibilityGroupId != null &&
    permittedGroups.includes(effectiveVisibilityGroupId)
  );
}

export function checkIsWinLossEnabled(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
): boolean {
  const isCurator = checkCuratorAccess(user, visibilityGroup);

  // Explicitly check if companyData properties are undefined. If they are, use userData values.
  const winlossEnabled =
    company?.companyData?.isWinLossEnabled !== undefined
      ? company.companyData.isWinLossEnabled
      : user?.userData?.isWinLossEnabled;

  const consumerAccessEnabled =
    company?.companyData?.isWinLossConsumerAccessEnabled !== undefined
      ? company.companyData.isWinLossConsumerAccessEnabled
      : user?.userData?.isWinLossConsumerAccessEnabled;

  const userCanAccess = isCurator || consumerAccessEnabled;

  return Boolean(
    winlossEnabled &&
    userCanAccess &&
    checkWinLossVisibilityGroupPermitted(company, user, visibilityGroup),
  );
}

/**
 * Who may export a chat answer as a document: admins and curators always, and
 * consumers when an admin has switched it on in Chat Customization.
 *
 * Note this is unrelated to isAskKlueExportEnabled / …ForCurators, which gate
 * the Ask Klue usage report in Reports.
 */
export function checkIsChatExportEnabled(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
): boolean {
  if (!company || !user) return false;

  const isCurator = checkCuratorAccess(user, visibilityGroup);
  const consumerAccessEnabled = Boolean(
    company?.companyData?.isChatExportConsumerAccessEnabled,
  );

  return isCurator || consumerAccessEnabled;
}

export function checkIsWinLossExclusive(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
) {
  return (
    checkIsWinLossEnabled(company, user, visibilityGroup) &&
    Boolean(company?.companyData?.isWinLossExclusive)
  );
}

export function checkIsHatchExclusive(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
) {
  return (
    Boolean(company?.companyData?.isCompanyHatchExclusive) ||
    Boolean(user?.userData?.isHatchExclusive)
  );
}

export function checkIsCopyToBoardsEnabled(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
) {
  return Boolean(
    checkCuratorAccess(user, visibilityGroup) &&
    company?.companyData?.enableCopyToBoards,
  );
}

export function checkPATAccess(user: UserType | null) {
  return checkAdminAccess(user);
}

export function checkCompetitiveAnalyticsOverviewReportEnabled(
  user: UserType | null,
  company: CheckAccessCompanyType | null,
) {
  return Boolean(
    checkImpersonating(user) ||
    (checkAdminAccess(user) &&
      company?.companyData.isCompetitiveAnalyticsOverviewReportEnabled),
  );
}

export function checkCompetitiveAnalyticsThreatAnalysisReportEnabled(
  user: UserType | null,
  company: CheckAccessCompanyType | null,
) {
  const userHasAccess =
    (checkAdminAccess(user) || checkCuratorAccess(user)) &&
    checkIsCRAReportEnabledForCurators(company);
  return Boolean(
    checkImpersonating(user) ||
    (userHasAccess &&
      company?.companyData.isCompetitiveAnalyticsThreatAnalysisReportEnabled),
  );
}

export function checkCompetitiveAnalyticsRepPerformanceReportEnabled(
  user: UserType | null,
  company: CheckAccessCompanyType | null,
) {
  return Boolean(
    checkImpersonating(user) ||
    (checkAdminAccess(user) &&
      company?.companyData.isCompetitiveAnalyticsRepPerformanceReportEnabled),
  );
}

export function checkCompetitiveAnalyticsDataHygieneReportEnabled(
  user: UserType | null,
  company: CheckAccessCompanyType | null,
) {
  const userHasAccess =
    (checkAdminAccess(user) || checkCuratorAccess(user)) &&
    checkIsCRAReportEnabledForCurators(company);
  return Boolean(
    checkImpersonating(user) ||
    (userHasAccess &&
      company?.companyData.isCompetitiveAnalyticsDataHygieneReportEnabled),
  );
}

export function checkCompetitiveAnalyticsImpactReportEnabled(
  user: UserType | null,
  company: CheckAccessCompanyType | null,
) {
  return Boolean(
    checkImpersonating(user) ||
    (checkAdminAccess(user) &&
      company?.companyData.isCompetitiveAnalyticsImpactReportEnabled),
  );
}

export function checkIsCompetitiveAnalyticsProgramImpactReportEnabled(
  user: UserType | null,
  company: CheckAccessCompanyType | null,
) {
  return Boolean(
    checkImpersonating(user) ||
    (checkAdminAccess(user) &&
      company?.companyData.isCompetitiveAnalyticsProgramImpactReportEnabled),
  );
}

export function checkIsConsumerReportEnabledForCurators(
  company: CheckAccessCompanyType | null,
) {
  return Boolean(company?.companyData.isConsumerReportEnabledForCurators);
}

export function checkIsCuratorReportEnabledForCurators(
  company: CheckAccessCompanyType | null,
) {
  return Boolean(company?.companyData.isCuratorReportEnabledForCurators);
}

export function checkIsCRAReportEnabledForCurators(
  company: CheckAccessCompanyType | null,
) {
  return Boolean(company?.companyData.isCRAReportEnabledForCurators);
}

export function checkIsAskKlueExportEnabledForCurators(
  company: CheckAccessCompanyType | null,
) {
  return Boolean(company?.companyData.isAskKlueExportEnabledForCurators);
}

export function checkIsAskKlueExportEnabled(
  company: CheckAccessCompanyType | null,
) {
  return Boolean(company?.companyData.isAskKlueExportEnabled);
}

export function checkisCompetitiveDashboardEnabledForConsumers(
  company: CheckAccessCompanyType | null,
) {
  return Boolean(
    company?.companyData.isCompetitiveDashboardEnabledForConsumers,
  );
}

export function checkAskKlueEnabled(
  company: CompanyType | null,
  user: UserType | null,
  visibilityGroup: number | null = null,
) {
  // Early return false if company is null
  if (!company) {
    return false;
  }

  // If askKluePermittedVisibilityGroups is defined, it takes precedence
  const permittedGroups = company.companyData?.askKluePermittedVisibilityGroups;

  // Type safety check: only process if it's actually an array of numbers
  if (
    permittedGroups !== undefined &&
    Array.isArray(permittedGroups) &&
    permittedGroups.every((id) => typeof id === 'number')
  ) {
    // Empty array means no one has access
    if (permittedGroups.length === 0) {
      // Still check if curator preview is enabled
      return Boolean(
        company.companyData?.isDashboardAskKluePreviewForCuratorsEnabled &&
        checkCuratorAccess(user, visibilityGroup),
      );
    }

    // Determine the effective visibility group to check
    // If visibilityGroup is provided (preview mode), use that; otherwise use user's visibility group
    const effectiveVisibilityGroupId =
      visibilityGroup !== null ? visibilityGroup : user?.visibilityGroupId;

    const isInPermittedGroup =
      effectiveVisibilityGroupId != null &&
      permittedGroups.includes(effectiveVisibilityGroupId);

    // User has access if they're in a permitted group OR if curator preview is enabled for curators
    return Boolean(
      isInPermittedGroup ||
      (company.companyData?.isDashboardAskKluePreviewForCuratorsEnabled &&
        checkCuratorAccess(user, visibilityGroup)),
    );
  }

  // Fallback to original logic if askKluePermittedVisibilityGroups is not defined
  return Boolean(
    !company.companyData?.isDashboardAskKlueDisabled ||
    (company.companyData?.isDashboardAskKluePreviewForCuratorsEnabled &&
      checkCuratorAccess(user, visibilityGroup)),
  );
}

export function checkIsDashboardAskKlueEnabled(company: CompanyType | null) {
  return Boolean(!company?.companyData.isDashboardAskKlueDisabled);
}

export function checkIsDashboardAskKluePreviewForCuratorsEnabled(
  company: CompanyType | null,
  user: UserType | null,
) {
  return Boolean(
    company?.companyData?.isDashboardAskKluePreviewForCuratorsEnabled &&
    checkCuratorAccess(user),
  );
}

export function checkDashboardNewsToggleDefault(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isDashboardNewsToggleDefaultOff);
}

export function checkCompanyLandingPageEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isCompanyLandingPageEnabled);
}

export function checkCompanyFavoritesEnabled(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
) {
  /* Enabled for consumers only */
  const userHasAccess = !checkCuratorAccess(user, visibilityGroup);
  return (
    Boolean(company?.companyData?.isCompanyFavoritesEnabled) && userHasAccess
  );
}

export function checkUpdateSummaryEnabled(company: CheckAccessCompanyType) {
  return Boolean(company?.companyData?.isUpdateSummaryEnabled);
}

export function checkCompanyLinkDefault(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
): CompanyLinkDefaultType {
  const userHasAccess = !checkCuratorAccess(user, visibilityGroup);
  if (!userHasAccess) return 'profile';
  if (Boolean(company?.companyData?.isCompanyLinkDefaultLandingPage))
    return 'landing_page';
  if (Boolean(company?.companyData?.isCompanyLinkDefaultBattlecard))
    return 'battlecard';
  return 'profile';
}

export function checkIsGenAISearchResultsEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isGenAISearchResultsEnabled);
}

export function checkIsSFDCDataSyncUserRoleEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isSFDCDataSyncUserRoleEnabled);
}

export function checkIsSFDCOpportunityEmailEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isSFDSOpportunityEmailSyncEnabled);
}

export function checkIsFeedHiddenInConsumerNavEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isFeedHiddenInConsumerNavEnabled);
}

export function checkIsInsightsWhyWeWinLoseEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isInsightsWhyWeWinLoseEnabled);
}

export function checkIsCallInsightsDemoReplacementEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isCallInsightsDemoReplacementEnabled);
}

export function checkIfNewCardEditorEnabled(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
) {
  const hasCuratorAccess = checkCuratorAccess(user, visibilityGroup);
  const hasStaffAccess =
    checkImpersonatingOrStaffAccess(user) &&
    checkCuratorAccess(user, visibilityGroup);

  return Boolean(
    (hasCuratorAccess || hasStaffAccess) &&
    company?.companyData?.enableNewCardEditor,
  );
}

export function checkIfContentEditorV3Enabled(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
) {
  const hasCuratorAccess = checkCuratorAccess(user, visibilityGroup);
  const hasStaffAccess =
    checkImpersonatingOrStaffAccess(user) &&
    checkCuratorAccess(user, visibilityGroup);

  return Boolean(
    (hasCuratorAccess || hasStaffAccess) &&
    company?.companyData?.enableContentEditorV3,
  );
}

export function checkIfCreateAIBattlecardEnabled(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null = null,
) {
  const hasCuratorAccess = checkCuratorAccess(user, visibilityGroup);
  const hasStaffAccess =
    checkImpersonatingOrStaffAccess(user) &&
    checkCuratorAccess(user, visibilityGroup);

  return Boolean(
    (hasCuratorAccess || hasStaffAccess) &&
    company?.companyData?.enableCreateAIBattlecard,
  );
}

export function checkIsEditorTemplateBlocksEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.enableEditorTemplateBlocks);
}

export function checkIsSFDSProductMappingEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isSFDSProductMappingEnabled);
}

export function checkIsHSDSProductMappingEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isHSDSProductMappingEnabled);
}

export function checkIsSFDSWriteBackEnabled(company: CheckAccessCompanyType) {
  return Boolean(company?.companyData?.isSFDSWriteBackEnabled);
}

export function checkIsHSDSWriteBackEnabled(company: CheckAccessCompanyType) {
  return Boolean(company?.companyData?.isHSDSWriteBackEnabled);
}

export function checkIsHSDSMeetingsSyncEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isHSDSMeetingsSyncEnabled);
}

export function checkIsAskKlueWizardEnabled(company: CheckAccessCompanyType) {
  return Boolean(company?.companyData?.isAskKlueWizardEnabled);
}

export function checkIsConsumerAskKlueToggleHidden(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isConsumerAskKlueToggleHidden);
}

export function checkKnowledgeHubEnabled(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
  visibilityGroup: number | null,
) {
  const hasCuratorAccess = checkCuratorAccess(user, visibilityGroup);
  const hasStaffAccess =
    checkImpersonatingOrStaffAccess(user) &&
    checkCuratorAccess(user, visibilityGroup);

  return Boolean(
    (hasCuratorAccess || hasStaffAccess) &&
    company?.companyData?.isKnowledgeHubEnabled,
  );
}

export function checkIsBattlecardAskKlueEnabled(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isBattlecardAskKlueEnabled);
}

export function checkDemoSettingsAccess(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
) {
  const isFeatureEnabled = Boolean(company?.companyData?.isDemoSettingsEnabled);
  return isFeatureEnabled && checkAdminAccess(user);
}

export function checkNotificationPreferencesAccess(
  company: CheckAccessCompanyType,
  user: CheckAccessUserType,
) {
  const isFeatureEnabled = Boolean(
    company?.companyData?.isNotificationSettingsEnabled,
  );
  // Open to all signed-in users; they manage their own preferences.
  return isFeatureEnabled && !!user;
}

export function checkIsUnifiedSearchEnabled(company: CheckAccessCompanyType) {
  return Boolean(company?.companyData?.isUnifiedSearchEnabled);
}

export function checkIsAskKlueDashboardPageEnabled(
  company: CheckAccessCompanyType,
) {
  return (
    Boolean((company?.companyData as any)?.isAskKlueDashboardPageEnabled) &&
    Boolean(!(company?.companyData as any)?.isDashboardAskKlueDisabled)
  );
}

export function checkIsPromptLibraryEnabled(company: CheckAccessCompanyType) {
  return Boolean(company?.companyData?.isPromptLibraryEnabled);
}

export function checkIsZoomStatusSettingsVisible(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isZoomStatusSettingsVisible);
}

export function checkIsGoogleCalendarDataSyncVisible(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isGoogleCalendarDataSyncVisible);
}

export function checkIsGoogleMeetDataSyncVisible(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isGoogleMeetDataSyncVisible);
}

export function checkIsGoogleMeetDataSyncInstallable(
  company: CheckAccessCompanyType,
) {
  return Boolean(company?.companyData?.isGoogleMeetDataSyncInstallable);
}

export function checkIsCallInsightsDisabled(company: CheckAccessCompanyType) {
  return Boolean(company?.companyData?.isCallInsightsDisabled);
}

export function checkIsDealTipsEnabled(company: CheckAccessCompanyType) {
  return Boolean(company?.companyData?.isDealTipsEnabled);
}
